import React, { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { gql, useQuery } from '@apollo/client'
import { Box, Container, Modal } from '@mui/material'
import Header from '../components/Headers/Header'
import useGlobalStyles from '../utils/globalStyles'
import { getAllOrdersForSuperAdmin } from '../apollo'
import SuperAdminOrder from '../components/Order/SuperAdminOrder'
import SuperAdminOrdersData from '../components/Order/SuperAdminOrdersData'
import { modalOverlay, modalCard } from '../utils/modalStyles'
import { useNotifications } from '../context/Notifications'

const GET_SUPER_ADMIN_ORDERS = gql`
  ${getAllOrdersForSuperAdmin}
`

const SEARCH_DEBOUNCE_MS = 400

const SuperAdminOrders = () => {
  const [detailsModal, setDetailModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [limit, setLimit] = useState(10)
  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [search, setSearch] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [sortBy, setSortBy] = useState('')

  // The search runs on the server (order ID or voucher code), so hold off until
  // typing settles and start again from page one on every new term.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchQuery.trim())
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // The range is filtered server side, so a new range restarts at page one.
  const handleDateRangeChange = (nextStart, nextEnd) => {
    setStartDate(nextStart)
    setEndDate(nextEnd)
    setPage(1)
  }

  // Sorting is server side too, so the ranking spans every page.
  const handleSortChange = nextSort => {
    setSortBy(nextSort)
    setPage(1)
  }

  const { data, error, loading, previousData, refetch } = useQuery(
    GET_SUPER_ADMIN_ORDERS,
    {
      variables: {
        page,
        rows: limit,
        search,
        startDate: startDate || null,
        endDate: endDate || null,
        sortBy: sortBy || null
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true
    }
  )

  // The notifications feed spots new orders; reload the list when it does, so
  // staff no longer have to refresh the page by hand.
  const { lastNewOrderAt } = useNotifications()
  useEffect(() => {
    if (lastNewOrderAt) refetch()
  }, [lastNewOrderAt, refetch])

  const globalClasses = useGlobalStyles()
  const orders =
    data?.allOrdersPaginated?.orders ??
    previousData?.allOrdersPaginated?.orders ??
    []
  const totalCount =
    data?.allOrdersPaginated?.totalCount ??
    previousData?.allOrdersPaginated?.totalCount ??
    0

  const toggleModal = order => {
    setSelectedOrder(order)
    setDetailModal(previous => !previous)
  }

  return (
    <>
      <Header />
      <Container
        maxWidth={false}
        disableGutters
        className={globalClasses.flex}
        sx={{ width: '100%', px: 3 }}>
        {error && <p>{`${'Error'} ${error.message}`}</p>}
        <SuperAdminOrdersData
          orders={orders}
          totalCount={totalCount}
          loading={loading}
          limit={limit}
          page={page}
          onPageChange={setPage}
          toggleModal={toggleModal}
          onLimitChange={setLimit}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          startDate={startDate}
          endDate={endDate}
          onDateRangeChange={handleDateRangeChange}
          sortBy={sortBy}
          onSortChange={handleSortChange}
        />
        <Modal
          open={detailsModal}
          onClose={() => {
            toggleModal(null)
          }}
          sx={modalOverlay}>
          <Box sx={modalCard('lg')}>
            <SuperAdminOrder
              order={selectedOrder}
              onClose={() => toggleModal(null)}
            />
          </Box>
        </Modal>
      </Container>
    </>
  )
}

export default withTranslation()(SuperAdminOrders)
